<template>
    <div class="data-status-grid">
        <loading-spinner :show-loading="showLoading"></loading-spinner>
        <information-modal
            :title="selectedErrorCode"
            v-model="showErrorDescriptionModal">
            <template #body>
                <div v-html="selectedErrorBody"></div>
            </template>
        </information-modal>

        <errors-warnings-update
            v-model="showUpdateModal"
            :item="selectedItem"
            :url="`${apiUrl}/actions/dismiss-error-warning`"
            :dataset="dataset"
            @dismiss="handleUpdateModalDismiss"></errors-warnings-update>

        <div class="d-flex justify-content-between align-items-end">
            <div class="form-check d-inline-block">
                <input
                    id="cbAddCol"
                    type="checkbox"
                    class="form-check-input"
                    v-model="showAdditionalColumns" />
                <label
                    for="cbAddCol"
                    class="form-check-label"
                    >Display with the latest explanation/justification for Status change</label
                >
            </div>
            <div
                class="btn-group ms-auto"
                style=""
                role="group">
                <drop-down
                    label="Error/Warning Details"
                    :item-count="2"
                    :is-primary-button="false">
                    <template v-slot:item1="{}">
                        <a
                            class="dropdown-item"
                            :href="`${store.errorDescriptionsDownloadUrl}csv`"
                            >Download as .csv</a
                        >
                    </template>
                    <template v-slot:item2="{}">
                        <a
                            class="dropdown-item"
                            :href="`${store.errorDescriptionsDownloadUrl}xlsx`"
                            >Download as .xlsx</a
                        >
                    </template>
                </drop-down>
            </div>
        </div>
        <b-grid
            caption="List of errors and warnings"
            :tableOptions="options"
            :filterableFields="filterableFields"
            :filterOptions="filterOptions"
            @optionChange="getData"
            :items="dataItems"
            :columns="columns"
            :loading="false"
            :current-item-count="numberOfReturnedItems">
            <template v-slot:cell.status="{item, column}">
                <button
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    @click.prevent="handleStatusClick(item)"
                    v-if="isStatusLinkEnabled(item)">
                    {{ item.status }}
                </button>
                <span v-else>{{ item.status }}</span>
            </template>
            <template #cell.incidentNumber="{item}">
                <a
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    :href="
                        getEditorPageName(getValueFromSource(item, 'incidentNumber'), getValueFromSource(item, 'ori'))
                    "
                    v-if="enableLinkToEditor"
                    >{{ item["incidentNumber"] }}</a
                >
                <span v-if="!enableLinkToEditor">{{ item["incidentNumber"] }}</span>
            </template>
            <template #cell.errorCode="{item}">
                <button
                    type="button"
                    class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                    v-on:click.prevent="handleErrorCodeClick(item)">
                    {{ item["errorCode"] }}
                </button>
            </template>
            <template #cell.vendorName="{item}">
                <p>{{ item.vendorName == null || item.vendorName?.length == 0 ? "Vendor Not Specified": item.vendorName }}</p>
            </template>
            <template #pagingStart>
                <div
                    class="btn-group"
                    role="group">
                    <drop-down
                        label="Export Data"
                        :item-count="2"
                        :is-primary-button="true">
                        <template v-slot:item1="{item}">
                            <a class="dropdown-item"
                               href="javascript:void"
                               @click.prevent="exportData('csv')">Download as .csv</a>
                        </template>
                        <template v-slot:item2="{item}">
                            <a class="dropdown-item"
                                href="javascript:void"
                                @click.prevent="exportData('xlsx')">Download as .xlsx</a>
                        </template>
                    </drop-down>
                </div>
            </template>
        </b-grid>
        <system-alerts></system-alerts>
    </div>
</template>
<script lang="ts" setup>
import {ref, computed, onMounted, watch} from "vue";
import axios from "axios";
import BGrid from "./BGrid/BGrid.vue";
import {serializeFilters, serializeSortBy} from "./BTable/HelperFunctions";
import LoadingSpinner from "./Misc/LoadingSpinner.vue";
import {BFilter} from "./BFilterEditor/BFilterEditor.types";
import {debounce} from "vue-debounce";
import {InformationModal, SystemAlerts} from "./Misc";
import {UploadType} from "@/types";
import {getHtmlContent} from "@/utils";
import {useCommunityPolicingStore} from "@/stores/communityPolicingStore";
import {useContactsStore} from "@/stores/contactsStore";
import {useNibrsStore} from "@/stores/nibrsStore";
import {useUofStore} from "@/stores/uofStore";
import {useSummaryStore} from "@/stores/summaryStore";
import {usePoliceEmployeeStore} from "@/stores/policeEmployeeStore";
import {usePopulationStore} from "@/stores/populationStore";
import DropDown from "@/components/Misc/DropDown.vue";
import {useDcraStore} from "@/stores/dcraStore";

// Props
const props = defineProps({
    url: String,
    title: String,
    dataset: String,
    onlyHasOneOri: Boolean,
    enableLinkToEditor: Boolean,
    hasClearError: Boolean,
    hasClearWarning: Boolean,
    editorUrl: String,
    noTotal: Boolean,
});

// State
const dataItems = ref([]);
const numberOfReturnedItems = ref(0);
const isShowFilterEditor = ref(false);
const total = ref(0);
const showLoading = ref(false);
const showUpdateModal = ref(false);
const selectedItem = ref({});
const showErrorDescriptionModal = ref(false);
const selectedErrorDescription = ref("");
const selectedErrorCode = ref("");
const selectedErrorType = ref("");
const selectedErrorBody = ref("");
const showAdditionalColumns = ref(false);

// Store setup
const uploadType = UploadType[props.dataset ?? ""];
const store = (() => {
    switch (uploadType) {
        case UploadType.contacts:
            return useContactsStore();
        case UploadType.communityPolicing:
            return useCommunityPolicingStore();
        case UploadType.nibrs:
            return useNibrsStore();
        case UploadType.summary:
            return useSummaryStore();
        case UploadType.useOfForce:
            return useUofStore();
        case UploadType.policeEmployee:
            return usePoliceEmployeeStore();
        case UploadType.population:
            return usePopulationStore();
        case UploadType.dcra:
            return useDcraStore();
    }
})();

const apiUrl = store!.apiUrl;

const getValueFromSource = (source, target): string => {
    if (source && target) {
        let value = source[target];
        if (value) {
            return encodeURIComponent(value);
        }
    }
    return "";
};

const getEditorPageName = (incidentNumber: string, ori: string): string => {
    if (props.editorUrl != undefined) {
        var editorUrl = new URL(props.editorUrl)!;

        editorUrl.searchParams.append("incident", incidentNumber);
        editorUrl.searchParams.append("ori", ori);
        editorUrl.searchParams.append("returnurl", window.location.href);

        return editorUrl.href;
    }

    return "";
};

// Filter options
const filtersJson = sessionStorage.getItem(`ew_${uploadType}_filterOptions`);
const filterOptions = ref(
    filtersJson
        ? JSON.parse(filtersJson)
        : {
              filters: [{property: "status", operation: "eq", value: "Unresolved"}],
          }
);

// Current paging mode
const currentPagingMode = computed(() => {
    if (!props.noTotal || filterOptions.value.filters.some((a) => a.property == "status" && a.value == "Unresolved")) {
        return "withTotal";
    }
    return "noTotal";
});

// Table options
const optionsJson = sessionStorage.getItem(`ew_${uploadType}_tableOptions`);
const options = ref(
    optionsJson
        ? JSON.parse(optionsJson)
        : {
              totalItemCount: 0,
              itemsPerPage: 25,
              page: 1,
              sortBy: [],
              pagingMode: currentPagingMode.value,
              isLastPage: false,
          }
);

// Computed properties
const filterableFields = computed(() =>
    store!.getFilterableFieldsForErrorsAndWarnings(props.onlyHasOneOri, showAdditionalColumns.value, false)
);

const columns = computed(() =>
    store!.getErrorsAndWarningsColumns(props.onlyHasOneOri, showAdditionalColumns.value, false)
);

const hasAppliedFilters = ref(filterOptions.value.filters.length > 0);

// Methods
const getData = async () => {
    if (isShowFilterEditor.value) return;

    showLoading.value = true;
    options.value.pagingMode = currentPagingMode.value;

    try {
        const response = await store!.getErrorsAndWarnings(
            filterOptions.value.filters,
            options.value,
            filterableFields.value,
            options.value.sortBy
        );

        const pagingMode = response?.data?.pagingMode ?? currentPagingMode.value;
        if (pagingMode == "noTotal" && response?.data?.data?.length > 0) {
            numberOfReturnedItems.value = response!.data.data.length;
            dataItems.value = response!.data.data.slice(0, options.value.itemsPerPage);
        } else {
            dataItems.value = response!.data.data;
        }

        total.value = response!.data.total;
        options.value.totalItemCount = total.value;
    } catch (error) {
        console.error(error?.response?.statusText);
    } finally {
        showLoading.value = false;
        updateTableDataSource();
    }
};

const handleUpdateModalDismiss = async () => {
    showUpdateModal.value = false;
    await getData();
};

const updateTableDataSource = debounce(() => getData(), "30000ms");

const isStatusLinkEnabled = (item: any) => {
    return (
        (item["errorType"] == "Warning" && props.hasClearWarning) ||
        (item["errorType"] == "Error" && props.hasClearError)
    );
};

const exportData = (fileType: string) => {
    let filters = [] as BFilter[];
    if (hasAppliedFilters.value) {
        filters = filterOptions.value.filters;
    }

    let filterText = serializeFilters(filters, filterableFields.value);
    let sortText = serializeSortBy(options.value.sortBy);

    var fullUrl = props.url;
    fullUrl += `export/${fileType}`;
    axios
        .get(fullUrl!, {
            responseType: "arraybuffer",
            params: {filter: filterText, sort: sortText},
        })
        .then(function (response) {
            var blob = new Blob([response.data], {
                type: response.headers["content-type"],
            });
            var link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = response.headers["content-disposition"].substring(
                response.headers["content-disposition"].indexOf("filename=") + 9
            );
            link.click();
        });
};

const handleStatusClick = (item: any) => {
    if (item != null) {
        var idNumber = props.dataset == "communityPolicing" ? item["stopNumber"] : item["incidentNumber"];

        selectedItem.value = item;
        showUpdateModal.value = true;
    }
};

const handleErrorCodeClick = (item: any) => {
    let errorCode = item["errorCode"];
    selectedErrorCode.value = "";
    selectedErrorDescription.value = "";
    selectedErrorBody.value = "No information available";

    if (errorCode != undefined && store!.errorDescriptions && store!.errorDescriptions.value) {
        selectedErrorCode.value = errorCode;
        store!.errorDescriptions.value.forEach((f) => {
            if (f.code == errorCode) {
                selectedErrorType.value = item["errorType"];
                selectedErrorDescription.value = f.help;

                var descriptionText = "";

                getHtmlContent(`help.aspx?e=${selectedErrorCode.value}`)
                    .then((data) => (descriptionText = data))
                    .catch((err) => (descriptionText = ""))
                    .finally(() => {
                        if (descriptionText != "") {
                            selectedErrorBody.value = descriptionText;
                        } else if (selectedErrorDescription.value != "") {
                            selectedErrorBody.value = `${selectedErrorDescription.value}`;
                        } else {
                            selectedErrorBody.value = "No information available";
                        }
                        showErrorDescriptionModal.value = true;
                        return;
                    });
            }
        });
        showErrorDescriptionModal.value = true;
    }
};

// Watchers
watch(
    () => options.value,
    () => {
        sessionStorage.setItem(`ew_${uploadType}_tableOptions`, JSON.stringify(options.value));
    },
    {deep: true}
);

watch(
    () => filterOptions.value,
    () => {
        sessionStorage.setItem(`ew_${uploadType}_filterOptions`, JSON.stringify(filterOptions.value));
        options.value.pagingMode = currentPagingMode.value;
    },
    {deep: true}
);

// Lifecycle hooks
onMounted(() => {
    getData();
});
</script>
